import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsStatCard from "../clients/Tools/kit/ToolsStatCard";
import ToolsPager from "../clients/Tools/kit/ToolsPager";
import SearchInput from "../common/search_input/SearchInput";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import PlaybookResultsService from "../../services/PlaybookResultsService";
import PlaybookRunService from "../../services/PlaybookRunService";
import UtilityService from "../../services/UtilityService";
import AgentRunTable from "./AgentRunTable";

import "./agent-results.css";

const SEARCH_DELAY_MILLISECONDS = 300;

const SEGMENT_ICONS = {
	"": Icon.Layers,
	needs_decisions: Icon.AlertCircle,
	waiting_on_signoff: Icon.Edit3,
	done: Icon.CheckCircle,
	no_action: Icon.Check,
	worth_a_look: Icon.Eye,
	did_not_finish: Icon.XCircle,
	skipped: Icon.SkipForward,
	in_progress: Icon.Clock,
};

const STAT_TONES = {
	"": "accent",
	no_action: "neutral",
};

class AgentResults extends WComponent {
	constructor(props) {
		super(props);
		const filters = PlaybookResultsService.filtersFromQuery({ search: props.location.search });
		this.state = {
			filters,
			searchDraft: filters.searchTerm,
			options: { teams: [], people: [], agents: [], periods: [], startedBy: [], bookedBy: [] },
			runs: [],
			total: 0,
			summary: null,
			isLoading: true,
			isRefreshing: false,
			loadError: null,
			optionsError: null,
		};
		this.searchTimer = null;
		this.requestNumber = 0;
	}

	componentDidMount() {
		this.fetchOptions();
		this.fetchRuns();
	}

	componentWillUnmount() {
		if (this.searchTimer) clearTimeout(this.searchTimer);
	}

	fetchOptions = async () => {
		try {
			const options = await PlaybookResultsService.listFilterOptions();
			await this.update({ options, optionsError: null });
		} catch (error) {
			await this.update({ optionsError: UtilityService.describeRequestError({ error, fallback: "The filter choices could not be loaded." }) });
		}
	};

	isFilterShown = ({ choiceCount, filterKey }) => {
		const hasChoice = choiceCount > 1;
		const isActive = !!this.state.filters[filterKey];
		const isShown = hasChoice || isActive;
		return isShown;
	};

	fetchRuns = async () => {
		const { filters } = this.state;
		this.requestNumber += 1;
		const thisRequest = this.requestNumber;
		await this.update({ isRefreshing: true });
		try {
			const page = await PlaybookResultsService.listRuns({ filters });
			if (thisRequest !== this.requestNumber) return;
			await this.update({ runs: page.runs, total: page.total, summary: page.summary, isLoading: false, isRefreshing: false, loadError: null });
		} catch (error) {
			if (thisRequest !== this.requestNumber) return;
			await this.update({ isLoading: false, isRefreshing: false, loadError: "The results could not be loaded. Try again in a moment." });
		}
	};

	applyFilters = async ({ changes, keepPage = false }) => {
		const filters = { ...this.state.filters, ...changes };
		if (!keepPage) filters.page = 1;
		const { history, location } = this.props;
		history.replace({ pathname: location.pathname, search: PlaybookResultsService.queryFromFilters({ filters }) });
		await this.update({ filters });
		this.fetchRuns();
	};

	onSearchTyped = event => {
		const searchDraft = event.target.value;
		this.update({ searchDraft });
		if (this.searchTimer) clearTimeout(this.searchTimer);
		this.searchTimer = setTimeout(() => this.applyFilters({ changes: { searchTerm: searchDraft.trim() } }), SEARCH_DELAY_MILLISECONDS);
	};

	onClearFilters = () => {
		const defaults = PlaybookResultsService.defaultResultFilters();
		this.update({ searchDraft: "" });
		this.applyFilters({ changes: { ...defaults, segment: this.state.filters.segment, sort: this.state.filters.sort } });
	};

	onSelectSegment = ({ segment }) => {
		const isAlreadyActive = this.state.filters.segment === segment;
		this.applyFilters({ changes: { segment: isAlreadyActive && segment ? "" : segment } });
	};

	onSort = ({ sort }) => {
		this.applyFilters({ changes: { sort } });
	};

	onChangePage = ({ page }) => {
		this.applyFilters({ changes: { page }, keepPage: true });
	};

	onStartedChosen = started => {
		const changes = started === "custom" ? { started } : { started, startedFrom: "", startedTo: "" };
		this.applyFilters({ changes });
	};

	onViewChosen = choice => {
		this.applyFilters({ changes: { view: PlaybookResultsService.viewFromFilterChoice({ choice }) } });
	};

	onToggleWaitingOnMe = () => {
		const isOn = this.state.filters.waitingOnMe === "yes";
		this.applyFilters({ changes: { waitingOnMe: isOn ? "" : "yes" } });
	};

	renderStatCard = segment => {
		const { summary, filters } = this.state;
		const count = summary ? summary[segment.key || "total"] : null;
		const isActive = filters.segment === segment.key;
		const isHiddenWhenEmpty = PlaybookResultsService.isExceptionSegment({ segment: segment.key });
		if (isHiddenWhenEmpty && !count && !isActive) return null;
		const delta = PlaybookResultsService.describeSegmentDelta({ segment: segment.key, summary });
		const SegmentIcon = SEGMENT_ICONS[segment.key] || Icon.Circle;

		return (
			<ToolsStatCard
				key={segment.key || "all"}
				icon={<SegmentIcon size={14} />}
				tone={STAT_TONES[segment.key] || segment.tone}
				label={segment.label}
				value={count}
				delta={delta ? delta.text : null}
				deltaTone={delta ? delta.tone : "neutral"}
				isActive={isActive}
				isEmpty={count === 0}
				onClick={() => this.onSelectSegment({ segment: segment.key })}
			/>
		);
	};

	renderStatStrip() {
		const everydaySegments = PlaybookResultsService.resultSegments().filter(segment => !PlaybookResultsService.isExceptionSegment({ segment: segment.key }));
		return <div className="tools-stat-strip">{everydaySegments.map(this.renderStatCard)}</div>;
	}

	renderExceptionPill = segment => {
		const { summary, filters } = this.state;
		const count = summary ? summary[segment.key] : 0;
		const isActive = filters.segment === segment.key;
		if (!count && !isActive) return null;

		return (
			<button
				key={segment.key}
				type="button"
				className={clsx("agent-results__pill", isActive && "agent-results__pill--on")}
				onClick={() => this.onSelectSegment({ segment: segment.key })}
				aria-pressed={isActive}
			>
				<span className={`tk-dot tk-dot--${segment.tone}`} />
				{segment.label}
				<span className="agent-results__pill-count">{count}</span>
			</button>
		);
	};

	renderExceptionPills() {
		const exceptionSegments = PlaybookResultsService.resultSegments().filter(segment => PlaybookResultsService.isExceptionSegment({ segment: segment.key }));
		return exceptionSegments.map(this.renderExceptionPill);
	}

	renderViews() {
		const { filters } = this.state;
		const isWaitingOnMe = filters.waitingOnMe === "yes";

		return (
			<div className="agent-results__views">
				<button
					type="button"
					className={clsx("agent-results__pill", isWaitingOnMe && "agent-results__pill--on")}
					onClick={this.onToggleWaitingOnMe}
					aria-pressed={isWaitingOnMe}
					title="Runs where you decide or sign off and it's your turn"
				>
					<Icon.User size={13} />
					Waiting on me
				</button>
				{this.renderExceptionPills()}
			</div>
		);
	}

	renderFilters() {
		const { filters, options, searchDraft } = this.state;
		const fixedChoices = PlaybookResultsService.fixedFilterChoices();
		const teamChoices = [{ value: "", label: "Any team" }, ...options.teams.map(team => ({ value: String(team.id), label: team.name }))];
		const personChoices = [
			{ value: "", label: "Anyone" },
			...options.people.map(person => ({ value: String(person.id), label: person.name, hint: person.roles.join(" · ") })),
		];
		const periodChoices = [
			{ value: "", label: "Any period" },
			...options.periods.map(periodEnd => ({ value: periodEnd, label: PlaybookRunService.periodLabelFor({ periodEnd, isLong: true }) })),
		];
		const starterChoices = [{ value: "", label: "Anyone" }, ...options.startedBy.map(person => ({ value: String(person.id), label: person.name }))];
		const bookerChoices = [{ value: "", label: "Anyone" }, ...options.bookedBy.map(person => ({ value: String(person.id), label: person.name }))];
		const agentChoices = [{ value: "", label: "Any playbook" }, ...options.agents.map(agent => ({ value: String(agent.id), label: agent.name }))];
		const hasNarrowing = PlaybookResultsService.countNarrowingFilters({ filters }) > 0;

		return (
			<div className="tk-toolbar">
				<SearchInput value={searchDraft} onChange={this.onSearchTyped} placeholder="Search clients" />
				{this.isFilterShown({ choiceCount: options.teams.length, filterKey: "teamId" }) && (
					<ToolsSelect
						filter
						label="Team"
						value={filters.teamId}
						options={teamChoices}
						onChange={teamId => this.applyFilters({ changes: { teamId } })}
					/>
				)}
				{this.isFilterShown({ choiceCount: options.people.length, filterKey: "personId" }) && (
					<ToolsSelect
						filter
						searchable
						searchPlaceholder="Search people"
						label="Assigned to"
						value={filters.personId}
						options={personChoices}
						onChange={personId => this.applyFilters({ changes: { personId } })}
					/>
				)}
				{this.isFilterShown({ choiceCount: options.periods.length, filterKey: "periodEnd" }) && (
					<ToolsSelect
						filter
						label="Period"
						value={filters.periodEnd}
						options={periodChoices}
						onChange={periodEnd => this.applyFilters({ changes: { periodEnd } })}
					/>
				)}
				<ToolsSelect filter label="Started" value={filters.started} options={fixedChoices.started} onChange={this.onStartedChosen} />
				{filters.started === "custom" && (
					<span className="agent-results__dates">
						<input
							type="date"
							className="agent-results__date"
							value={filters.startedFrom}
							max={filters.startedTo || undefined}
							onChange={event => this.applyFilters({ changes: { startedFrom: event.target.value } })}
							aria-label="Started from"
						/>
						<span className="agent-results__dates-dash">to</span>
						<input
							type="date"
							className="agent-results__date"
							value={filters.startedTo}
							min={filters.startedFrom || undefined}
							onChange={event => this.applyFilters({ changes: { startedTo: event.target.value } })}
							aria-label="Started to"
						/>
					</span>
				)}
				<ToolsSelect
					filter
					label="Findings"
					value={filters.findingsLevel}
					options={fixedChoices.findings}
					onChange={findingsLevel => this.applyFilters({ changes: { findingsLevel } })}
				/>
				{this.isFilterShown({ choiceCount: options.startedBy.length, filterKey: "startedByUserId" }) && (
					<ToolsSelect
						filter
						searchable
						searchPlaceholder="Search people"
						label="Started by"
						value={filters.startedByUserId}
						options={starterChoices}
						onChange={startedByUserId => this.applyFilters({ changes: { startedByUserId } })}
					/>
				)}
				{this.isFilterShown({ choiceCount: options.bookedBy.length, filterKey: "bookedByUserId" }) && (
					<ToolsSelect
						filter
						searchable
						searchPlaceholder="Search people"
						label="Booked by"
						value={filters.bookedByUserId}
						options={bookerChoices}
						onChange={bookedByUserId => this.applyFilters({ changes: { bookedByUserId } })}
					/>
				)}
				{this.isFilterShown({ choiceCount: options.agents.length, filterKey: "playbookId" }) && (
					<ToolsSelect
						filter
						label="Playbook"
						value={filters.playbookId}
						options={agentChoices}
						onChange={playbookId => this.applyFilters({ changes: { playbookId } })}
					/>
				)}
				<span className="tk-toolbar__end">
					{hasNarrowing && (
						<button type="button" className="tk-text-action" onClick={this.onClearFilters}>
							Clear filters
						</button>
					)}
					<ToolsSelect
						filter
						label="Show"
						value={PlaybookResultsService.viewFilterChoiceFor({ filters })}
						options={PlaybookResultsService.viewFilterChoices()}
						onChange={this.onViewChosen}
					/>
				</span>
			</div>
		);
	}

	renderPager() {
		const { filters, total, summary } = this.state;
		const pageSize = PlaybookResultsService.resultsPageSize();
		const pageCount = Math.max(1, Math.ceil(total / pageSize));
		const clientCount = summary ? summary.clients : 0;
		const scopeNote = filters.waitingOnMe === "yes" ? "every run waiting on you" : filters.view === "latest" ? "latest run per client" : "every run";
		const pagerSummary = `${UtilityService.describeCount({ count: clientCount, singular: "client" })} · ${scopeNote}`;

		return (
			<ToolsPager
				page={filters.page}
				pageCount={pageCount}
				pageSize={pageSize}
				total={total}
				noun="run"
				summary={pagerSummary}
				onChange={this.onChangePage}
			/>
		);
	}

	renderEmptyState() {
		const { filters } = this.state;
		const hasNarrowing = PlaybookResultsService.countNarrowingFilters({ filters }) > 0 || filters.segment;
		if (!hasNarrowing) return "No runs yet. Choose Run a playbook, or run one from a client's Tools → Playbooks.";
		return (
			<span>
				No runs match these filters.{" "}
				<button
					type="button"
					className="tk-text-action"
					onClick={() => this.applyFilters({ changes: { ...PlaybookResultsService.defaultResultFilters(), sort: filters.sort } })}
				>
					Show everything
				</button>
			</span>
		);
	}

	render() {
		const { onOpenRun } = this.props;
		const { runs, filters, options, isLoading, isRefreshing, loadError, optionsError } = this.state;

		return (
			<div className="agent-results">
				<p className="tk-page-intro">What the playbooks found on every client, newest first. Open a run to decide on its findings or sign it off.</p>
				{this.renderStatStrip()}
				{this.renderViews()}
				{this.renderFilters()}
				{optionsError && <div className="tk-notice tk-notice--danger agent-results__error">{optionsError}</div>}
				{loadError && <div className="tk-notice tk-notice--danger agent-results__error">{loadError}</div>}
				<AgentRunTable
					runs={runs}
					isLoading={isLoading}
					isRefreshing={isRefreshing}
					showClient={true}
					showAgent={options.agents.length > 1}
					sort={filters.sort}
					onSort={this.onSort}
					onOpenRun={onOpenRun}
					emptyState={this.renderEmptyState()}
				/>
				{this.renderPager()}
			</div>
		);
	}
}

export default AgentResults;
